import * as React from 'react';
import {connect} from 'react-redux';

import type {ListItem} from 'nutshell-core/types';
import {VisualPipeline} from 'nutshell-core/visual-pipeline';
import type {CloseType} from 'nutshell-core/visual-pipeline';

// @ts-expect-error
import {LoadingIcon} from 'shells/icon/svg/loading-icon';

import type {NutshellState} from '../../../store';

import {dropResultTypes} from '../../dnd/drag-item-types';
import type {LeadCardDropResult} from '../../dnd/drag-item-types';

import {VisualPipelineLeadCard} from '../card/visual-pipeline-lead-card';

import styles from './visual-pipeline-column-cards.module.css';

type OwnProps = {
    stageId: string;
    selectedSort: string;
    fieldsToDisplay: string[];
    isLoadingFields: boolean;
};

type StateProps = {
    isLoading: boolean;
    listItems: Array<ListItem>;
    canDragCards: boolean;
    cardIdChangingState: string | null | undefined;
};

type DispatchProps = {
    onAttemptLeadStageChange: (leadId: string, oldStageId: string, stageId: string) => any;
    onConfirmCloseLead: (leadId: string, stageId: CloseType) => any;
    onToggleCardDrag: (leadId: string) => any;
};

const mapStateToProps = function (state: NutshellState, ownProps: OwnProps): StateProps {
    const stageId = ownProps.stageId;
    const selectedSort = ownProps.selectedSort;

    return {
        isLoading: VisualPipeline.getIsLoadingForStage(stageId)(state),
        listItems: VisualPipeline.getListItemsForStage({stageId, selectedSort})(state),
        canDragCards: VisualPipeline.getCanDragCards(state),
        cardIdChangingState: VisualPipeline.getCardIdChangingState(state),
    };
};

// @ts-expect-error
const mapDispatchToProps = function (dispatch): DispatchProps {
    return {
        onAttemptLeadStageChange: (id, oldStageId, newStageId) => {
            dispatch(
                VisualPipeline.updateLeadStageTransition({
                    leadId: id,
                    oldStageId: oldStageId,
                    newStageId: newStageId,
                })
            );
        },
        onConfirmCloseLead: (id, closeType) => {
            dispatch(
                VisualPipeline.updateLeadClosingConfirmation({leadId: id, closeType: closeType})
            );
        },
        onToggleCardDrag: (id) => {
            dispatch(VisualPipeline.toggleCardIsDragging(id));
        },
    };
};

type Props = OwnProps & StateProps & DispatchProps;

export class VisualPipelineColumnCardsComponent extends React.PureComponent<Props> {
    render() {
        return (
            <div className={styles.container}>
                {this.props.listItems.map((listItem) => {
                    return (
                        <VisualPipelineLeadCard
                            key={listItem.links.entity.id}
                            listItem={listItem}
                            stageId={this.props.stageId}
                            canDrag={this.props.canDragCards}
                            isChangingState={
                                this.props.cardIdChangingState === listItem.links.entity.id
                            }
                            onBeginDrag={this.props.onToggleCardDrag}
                            onCardDrop={this.handleCardDrop}
                            fieldsToDisplay={this.props.fieldsToDisplay}
                            isLoadingFields={this.props.isLoadingFields}
                        />
                    );
                })}
                {this.props.isLoading ? (
                    <div className={styles['loading-overlay']}>
                        <div className={styles['overlay-background']} />
                        <LoadingIcon />
                    </div>
                ) : undefined}
            </div>
        );
    }

    handleCardDrop = (
        listItem: ListItem,
        currentStageId: string,
        dropResult?: LeadCardDropResult
    ) => {
        const leadId = listItem.links.entity.id;

        // Callback to update our state that a card has stopped being dragged
        this.props.onToggleCardDrag(leadId);

        if (dropResult && dropResult.type === dropResultTypes.PIPELINE_COLUMN) {
            const newStageId = dropResult.payload.stageId;

            // Card was not dropped on a different stage - do nothing
            if (currentStageId === newStageId) return;

            // Let our state know that we've dropped a lead in a new column
            // and started a transition. That way, we can show our confirmation
            // components, and prevent further stage transitions while this one
            // is still in progress
            this.props.onAttemptLeadStageChange(leadId, currentStageId, newStageId);
        } else if (dropResult && dropResult.type === dropResultTypes.CLOSE_BUTTON) {
            // We've dropped our card on either 'Cancel', 'Lose', or 'Win'
            // so we'll fire a callback that lets our state know we're now
            // in the 'Confirmation' state of closing a lead, so we can show
            // the correct dialog
            this.props.onConfirmCloseLead(leadId, dropResult.payload.closeType);
        }
    };
}

// @ts-expect-error
const connector = connect<Props, OwnProps, _, _, _, _>(mapStateToProps, mapDispatchToProps);
export const VisualPipelineColumnCards = connector(VisualPipelineColumnCardsComponent);
